import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { getArchivedDeals, unarchiveDeal } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Archive, ArchiveRestore, Eye, Loader2, FileText, DollarSign } from 'lucide-react';

export const ArchivedDealsPage = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unarchiving, setUnarchiving] = useState(null);

  const loadDeals = async () => {
    try {
      const res = await getArchivedDeals();
      setDeals(res.data || []);
    } catch (err) {
      toast.error('Failed to load archived deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeals();
  }, []);

  const handleUnarchive = async (dealId, dealNumber) => {
    setUnarchiving(dealId);
    try {
      await unarchiveDeal(dealId);
      toast.success(`Deal #${dealNumber} restored to active pipeline`);
      loadDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unarchive deal');
    } finally {
      setUnarchiving(null);
    }
  };

  return (
    <Layout>
      <Header
        title="Archived Deals"
        subtitle={`${deals.length} archived deal${deals.length !== 1 ? 's' : ''}`}
      />

      <div className="p-6">
        {loading ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <Loader2 size={32} className="mx-auto animate-spin text-slate-400 mb-2" />
            <p className="text-slate-500">Loading archived deals...</p>
          </div>
        ) : deals.length === 0 ? (
          <div className="bg-white border border-slate-200 p-12 text-center">
            <Archive size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">No Archived Deals</h3>
            <p className="text-slate-500 mb-4">
              Deals that are funded and then archived will appear here.
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Back to Dashboard
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Deal #</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Lender</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Archived</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => (
                    <tr key={deal.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-slate-900">{deal.deal_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{deal.customer_name}</p>
                          <p className="text-xs text-slate-500">{deal.finance_manager}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{deal.vehicle_info}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{deal.lender}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-emerald-700">
                          <DollarSign size={14} />
                          <span className="font-semibold">{deal.amount_financed?.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-500">
                          {deal.archived_at ? new Date(deal.archived_at).toLocaleDateString() : 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/deal/${deal.id}`)}
                            data-testid={`view-archived-${deal.id}`}
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUnarchive(deal.id, deal.deal_number)}
                            disabled={unarchiving === deal.id}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            data-testid={`unarchive-${deal.id}`}
                          >
                            {unarchiving === deal.id ? (
                              <Loader2 size={14} className="mr-1 animate-spin" />
                            ) : (
                              <ArchiveRestore size={14} className="mr-1" />
                            )}
                            Restore
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
